import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Images, Camera, Loader2, Upload, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const imgUrl = (path) => `${API}/gallery/file?path=${encodeURIComponent(path)}`;

const Slider = ({ item }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);

  const move = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  };

  return (
    <div
      ref={ref}
      data-testid={`gallery-slider-${item.id}`}
      className="relative w-full h-72 md:h-80 rounded-xl overflow-hidden select-none cursor-ew-resize border border-[#D8E2DC]"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        move(e.clientX);
      }}
      onPointerMove={(e) => e.buttons === 1 && move(e.clientX)}
    >
      <img src={imgUrl(item.after_path)} alt={`${item.title} after cleaning`} className="absolute inset-0 w-full h-full object-cover" draggable="false" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={imgUrl(item.before_path)} alt={`${item.title} before cleaning`} className="absolute inset-0 h-full object-cover" style={{ width: ref.current?.offsetWidth || "100%" }} draggable="false" />
      </div>
      <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_10px_rgba(0,0,0,0.5)]" style={{ left: `${pos}%` }}>
        <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#081C15] font-display font-black text-xs shadow-lg">
          ↔
        </span>
      </div>
      <span className="absolute top-3 left-3 bg-[#081C15]/80 text-white text-xs font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full">Before</span>
      <span className="absolute top-3 right-3 bg-[#D4AF37] text-[#081C15] text-xs font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full">After</span>
    </div>
  );
};

const UploadForm = ({ onDone }) => {
  const [code, setCode] = useState(() => localStorage.getItem("imr_admin_code") || "");
  const [title, setTitle] = useState("");
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!before || !after) {
      toast.error("Please choose both a before and an after photo.");
      return;
    }
    setBusy(true);
    try {
      const data = new FormData();
      data.append("title", title);
      data.append("before", before);
      data.append("after", after);
      await axios.post(`${API}/gallery`, data, { headers: { "x-admin-code": code } });
      localStorage.setItem("imr_admin_code", code);
      toast.success("Before/after photos published.");
      setTitle("");
      setBefore(null);
      setAfter(null);
      onDone();
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Wrong admin code." : "Upload failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-lg bg-white border border-[#D8E2DC] px-4 py-3 text-sm text-[#081C15] placeholder:text-gray-400 focus:ring-2 focus:ring-[#D4AF37] focus:outline-none transition duration-200";

  return (
    <form onSubmit={submit} data-testid="gallery-upload-form" className="space-y-4">
      <div>
        <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1B4332] mb-2">Admin Code</label>
        <input type="password" data-testid="gallery-admin-code" value={code} onChange={(e) => setCode(e.target.value)} required placeholder="Owner code" className={inputCls} />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1B4332] mb-2">Job Title</label>
        <input data-testid="gallery-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Broiler barn — Chilliwack" className={inputCls} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1B4332] mb-2">Before Photo *</label>
          <input type="file" accept="image/*" data-testid="gallery-before-input" onChange={(e) => setBefore(e.target.files[0])} required className="text-sm text-gray-600 file:mr-3 file:rounded-full file:border-0 file:bg-[#1B4332] file:text-white file:px-4 file:py-2 file:text-xs file:font-bold hover:file:bg-[#081C15] file:transition-colors" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1B4332] mb-2">After Photo *</label>
          <input type="file" accept="image/*" data-testid="gallery-after-input" onChange={(e) => setAfter(e.target.files[0])} required className="text-sm text-gray-600 file:mr-3 file:rounded-full file:border-0 file:bg-[#1B4332] file:text-white file:px-4 file:py-2 file:text-xs file:font-bold hover:file:bg-[#081C15] file:transition-colors" />
        </div>
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="gallery-upload-button"
        className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-white px-6 py-3 font-display font-bold text-sm hover:bg-[#081C15] disabled:opacity-60 transition-colors duration-200"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
        {busy ? "Uploading..." : "Publish Photos"}
      </button>
    </form>
  );
};

export const BeforeAfter = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setItems(res.data);
    } catch (err) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    const code = localStorage.getItem("imr_admin_code") || "";
    try {
      await axios.delete(`${API}/gallery/${id}`, { headers: { "x-admin-code": code } });
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Photo set removed.");
    } catch (err) {
      toast.error("Could not remove — check your admin code.");
    }
  };

  return (
    <section id="before-after" data-testid="before-after-section" className="bg-white py-20 md:py-28 border-t border-[#D8E2DC]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl mb-12">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#1B4332] mb-4">Proof of Work</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#081C15]" data-testid="gallery-heading">
            Before &amp; after, straight from the barn
          </h2>
          <p className="mt-4 text-base md:text-lg text-gray-700 leading-relaxed">
            Drag the slider on each photo to see the difference a professional IMR clean makes.
          </p>
          <button
            type="button"
            data-testid="gallery-owner-toggle"
            onClick={() => setShowUpload((v) => !v)}
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1B4332] hover:text-[#081C15] transition-colors duration-200"
          >
            {showUpload ? <X size={15} /> : <Camera size={15} />}
            {showUpload ? "Close owner upload" : "IMR team: add photos"}
          </button>
          {showUpload && (
            <div className="mt-6 rounded-xl border border-[#D8E2DC] bg-[#F9FAF8] p-6 md:p-8">
              <UploadForm onDone={() => load()} />
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-gray-500" data-testid="gallery-loading">
            <Loader2 size={18} className="animate-spin" /> Loading photos...
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#D8E2DC] bg-[#F9FAF8] p-10 text-center" data-testid="gallery-empty">
            <Images size={32} className="mx-auto text-[#1B4332] mb-3" />
            <p className="font-display font-bold text-lg text-[#081C15]">Photos coming soon</p>
            <p className="mt-2 text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
              We're adding before &amp; after photos from recent barn cleanings. Call
              1-970-902-3321 and we'll gladly share references from producers in your area.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8" data-testid="gallery-grid">
            {items.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: "easeOut" }}
              >
                <Slider item={item} />
                <div className="mt-3 flex items-center justify-between">
                  <p className="font-display font-bold text-[#081C15]" data-testid={`gallery-title-${item.id}`}>{item.title || "Barn cleaning"}</p>
                  {showUpload && (
                    <button onClick={() => remove(item.id)} data-testid={`gallery-delete-${item.id}`} className="text-red-600 hover:text-red-800 transition-colors" aria-label="Remove photo set">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-10">
          <button
            onClick={() => setShowUpload(!showUpload)}
            data-testid="gallery-owner-toggle"
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-[#1B4332] transition-colors duration-200"
          >
            {showUpload ? <X size={15} /> : <Camera size={15} />}
            {showUpload ? "Close owner upload" : "IMR team: add photos"}
          </button>
          {showUpload && (
            <div className="mt-6 max-w-xl rounded-xl border border-[#D8E2DC] bg-[#F9FAF8] p-6" data-testid="gallery-upload-panel">
              <UploadForm onDone={() => { load(); }} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
